// SPDX-License-Identifier: AGPL-3.0-or-later
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Dataset, StateRow } from '../types';
import { num, rate, pct, times, esc, RATE_RAMP, RATIO_RAMP, rampFor } from '../format';
import { openState } from '../main';

interface Measure {
  id: string; label: string; ramp: string[];
  get: (s: StateRow) => number | null; ref: number; fmt: (v: number | null) => string;
}

export function renderMap(root: HTMLElement, data: Dataset): void {
  const c = data.meta.counts;
  const measures: Measure[] = [
    { id: 'rate', label: 'Imprisonment rate', ramp: RATE_RAMP, get: (s) => s.rateNow, ref: c.rate, fmt: (v) => `${rate(v)} / 100k` },
    { id: 'atsi', label: 'Aboriginal & T.S.I. rate', ramp: RATE_RAMP, get: (s) => s.atsiRateStd, ref: c.atsiRateStd, fmt: (v) => `${rate(v, 0)} / 100k` },
    { id: 'ratio', label: 'Over-representation', ramp: RATIO_RAMP, get: (s) => s.ratioStd, ref: c.ratioStd, fmt: (v) => times(v) },
    { id: 'remand', label: 'Remand share', ramp: RATE_RAMP, get: (s) => s.remandShareNow, ref: c.remandShare, fmt: (v) => pct(v) },
  ];
  let measure = measures[0];

  root.innerHTML = `
    <div class="view-head">
      <h2>The map of imprisonment</h2>
      <p>Every state and territory shaded by the chosen measure, on real ABS boundaries. Colour bands are set relative to the national figure, so the same shade means the same thing on every measure. Click a state for its full breakdown.</p>
    </div>
    <div class="controls">
      <span class="control-label">Show</span>
      <div class="seg" role="tablist" aria-label="Map measure">
        ${measures.map((mm) => `<button data-measure="${mm.id}" aria-pressed="${mm.id === measure.id}">${esc(mm.label)}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="map-shell"><div class="map-canvas" id="map"></div></div>
      <div class="map-legend" id="map-legend"></div>
    </div>
  `;

  const canvas = root.querySelector('#map') as HTMLElement;
  const map = L.map(canvas, { minZoom: 3, maxZoom: 8, zoomControl: true, scrollWheelZoom: false });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data: ABS Prisoners in Australia 2025 · Boundaries: ABS ASGS (CC BY 4.0)',
    subdomains: 'abcd', minZoom: 3, maxZoom: 8,
  }).addTo(map);
  map.setView([-28.2, 134], 4);

  const byCode = new Map(data.states.map((s) => [s.code, s]));
  let layer: L.GeoJSON | null = null;

  const thresholds = () => [0.5, 0.8, 1.2, 2.0].map((f) => f * measure.ref);
  const colourFor = (s: StateRow | undefined) => (s ? rampFor(measure.get(s), thresholds(), measure.ramp) : 'var(--no-data)');

  async function ensureLayer(): Promise<L.GeoJSON> {
    if (layer) return layer;
    const geo = await fetch('data/states.geojson').then((r) => { if (!r.ok) throw new Error('boundaries failed'); return r.json(); });
    layer = L.geoJSON(geo, {
      style: (f: any) => ({ fillColor: colourFor(byCode.get(String(f!.properties.code))), fillOpacity: 0.86, color: '#ffffff', weight: 1 }),
      onEachFeature: (f: any, lyr: any) => {
        const s = byCode.get(String(f.properties.code));
        bindTip(lyr, s, f.properties.name);
        lyr.on({
          mouseover: () => lyr.setStyle({ weight: 2.4, color: '#1c2333' }),
          mouseout: () => layer!.resetStyle(lyr),
          click: () => { if (s) openState(s.code); },
        });
      },
    }).addTo(map);
    try { map.fitBounds(layer.getBounds(), { padding: [10, 10] }); } catch { /* keep default view */ }
    return layer;
  }

  function bindTip(lyr: any, s: StateRow | undefined, name: string): void {
    lyr.bindTooltip(
      `<strong>${esc(name)}</strong><br>${s ? `${esc(measure.label)}: ${measure.fmt(measure.get(s))}<br>${num(s.totalNow)} prisoners · rate ${rate(s.rateNow)}` : 'no data'}`,
      { sticky: true, className: 'map-tip' },
    );
  }

  function restyle(): void {
    if (!layer) return;
    layer.setStyle((f: any) => ({ fillColor: colourFor(byCode.get(String(f!.properties.code))), fillOpacity: 0.86, color: '#ffffff', weight: 1 }));
    layer.eachLayer((lyr: any) => { const s = byCode.get(String(lyr.feature.properties.code)); bindTip(lyr, s, lyr.feature.properties.name); });
  }

  function legend(): void {
    const el = root.querySelector('#map-legend') as HTMLElement;
    const th = thresholds();
    el.innerHTML =
      `<span class="legend-item">lower</span><span class="ramp">${measure.ramp.map((cl) => `<span style="background:${cl}"></span>`).join('')}</span><span class="legend-item">higher</span>` +
      `<span class="legend-item" style="color:var(--text-tertiary)">bands at ${th.map((t) => measure.fmt(t)).join(', ')} · national ${measure.fmt(measure.ref)}</span>`;
  }

  measures.forEach(() => {});
  root.querySelectorAll<HTMLButtonElement>('[data-measure]').forEach((b) =>
    b.addEventListener('click', () => {
      measure = measures.find((mm) => mm.id === b.dataset.measure)!;
      root.querySelectorAll<HTMLButtonElement>('[data-measure]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      restyle(); legend();
    }),
  );

  void ensureLayer().then(legend);

  const fit = () => map.invalidateSize();
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(fit, 400);
}
